/**
 * @fileoverview Input/output schemas for storyline-finder-v1.
 *
 * This agent searches provided text (from import source or chapter content)
 * for a specific storyline matching the user's description and priority.
 * It outputs a single storyline if found, or explicitly indicates that no
 * matching storyline exists in the text.
 *
 * Output is flat (3 levels max) for Gemini API compatibility.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { StructurePhaseSchema, StorylinePriorityEnum } from '@/domains/story-planning/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.loreContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.seriesContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.characters.schema)
  .merge(AGENT_SCHEMA_OBJECTS.worldElements.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .merge(AGENT_SCHEMA_OBJECTS.bannedNames.schema)
  .merge(AGENT_SCHEMA_OBJECTS.projectedWordCount.schema)
  .merge(AGENT_SCHEMA_OBJECTS.planningGenreGuidance.schema)
  .merge(AGENT_SCHEMA_OBJECTS.textInput.schema)
  .merge(z.object({
    existingStorylines: z.array(z.object({
      name: z.string(),
      priority: StorylinePriorityEnum,
      summary: z.string().optional(),
    })).optional().describe('Existing storylines in the project for context.'),
  }));

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

const StorylineBeatOutputSchema = z.object({
  storylinePhase: StructurePhaseSchema.describe(
    'The structural function this beat serves for THIS STORYLINE\'s internal arc.'
  ),
  globalPhase: StructurePhaseSchema.describe(
    'Where this beat falls in the OVERALL story timeline. ' +
    'For A-storylines, globalPhase always equals storylinePhase. ' +
    'For B/C storylines, globalPhase may differ.'
  ),
  order: z.number().min(0).default(0).describe(
    'Ordering index (integer >= 0) when multiple beats share the same globalPhase (0-based).'
  ),
  plotEvent: z.string().describe(
    'The concrete, observable event that occurs in the story world.'
  ),
  narrativeState: z.string().optional().describe(
    'The emotional or thematic state of this storyline thread at this moment.'
  ),
  goalProgress: z.number().min(-1).max(1).describe(
    'Progress toward the storyline\'s central objective (-1 to +1).'
  ),
});

const StoryQuestionOutputSchema = z.object({
  question: z.string().describe(
    'A dramatic question raised by this storyline (e.g., "Will Marion forgive Indy?").'
  ),
});

export const OutputSchema = z.object({
  found: z.boolean().describe(
    'Whether a matching storyline was found in the provided text. ' +
    'Set to false if the text does not contain evidence of the described storyline.'
  ),
  notFoundReason: z.string().optional().describe(
    'When found is false, a brief explanation of why no matching storyline could be identified in the text. ' +
    'Should reference what was searched for and what the text actually contains.'
  ),
  name: z.string().optional().describe(
    'Short, evocative name for the storyline (2-5 words). Required when found is true.'
  ),
  summary: z.string().optional().describe(
    'Brief description of the storyline arc (1-3 sentences). Required when found is true.'
  ),
  priority: StorylinePriorityEnum.optional().describe(
    'Storyline priority matching the user\'s requested type. Required when found is true.'
  ),
  beats: z.array(StorylineBeatOutputSchema).optional().describe(
    'Structural beats for this storyline. Required when found is true.'
  ),
  storyQuestions: z.array(StoryQuestionOutputSchema).optional().describe(
    'Dramatic questions that keep readers engaged. Required when found is true.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
